import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { generateAuditedClient } from "./audit/auditedClient";
import {
  CreateUserInput,
  GetTeamsByUserNameInput,
  Team,
  User,
} from "./models/API";
import MainPage from "./components/MainPage";
import { getTeamsWithPermissions } from "./graphql/queries";
import {
  AuthUser,
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  AuthSession,
} from "aws-amplify/auth";

import { createUser } from "./graphql/mutations";
import { useGlobal } from "./context/GlobalContext";
import { setAuditUser } from "./audit/auditContextStore";

const POST_AUTH_REDIRECT_KEY = "postAuthRedirect";

const setPostAuthRedirect = () => {
  // Preserve the full URL (path + query + hash) so deep-links like
  // ?view=wbr-callouts&week=2026-W19&report=foo.pdf survive the OAuth round-trip.
  const fullUrl =
    window.location.pathname + window.location.search + window.location.hash;
  window.sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, fullUrl);
};

const consumePostAuthRedirect = () => {
  const stored = window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
  if (!stored) return;
  window.sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  // Only restore if the current URL is missing the saved query/hash
  // (Cognito strips them on callback to the redirectSignIn origin).
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  if (current !== stored) {
    window.history.replaceState({}, "", stored);
  }
};

const App = () => {
  const [user, setUser] = useState("");
  const [defaultTeamId, setdefaultTeamId] = useState(-1);
  const [teamConfig, setTeamConfig] = useState<Team>({
    __typename: "Team",
    id: -1,
    name: "",
    custom_attributes: "",
  });
  const [userConfig, setUserConfig] = useState<User | null>(null);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const { setGlobalTeamId } = useGlobal();

  const client = generateAuditedClient();

  const createMainPage = () => {
    if (defaultTeamId === -1)
      return (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress />
        </div>
      );
    return (
      <MainPage
        currentUserName={user}
        userConfig={userConfig}
        defaultTeamId={defaultTeamId}
        defaultTeamConfig={teamConfig}
      />
    );
  };

  const getTeams = async (currentUser: string) => {
    let team: Team = {
      __typename: "Team",
      id: -1,
      name: "",
      custom_attributes: "",
    };
    if (currentUser === "") return team;

    try {
      const createUserInput: CreateUserInput = {
        user_name: currentUser,
      };
      const user = await client.graphql({
        query: createUser,
        variables: {
          input: createUserInput,
        },
      });
      setUserConfig(user.data.createUser);
      // Bridge the resolved user identity into the audit context store so the
      // shared Amplify header builder can attribute subsequent operations to
      // this alias + numeric user_id (Req 1.1, 1.4). On the auth-failure path
      // this is never reached, so the store keeps its sentinel defaults (Req 1.5).
      setAuditUser(currentUser, user.data.createUser.id);
      const input: GetTeamsByUserNameInput = {
        user_name: currentUser,
      };
      const result = await client.graphql({
        query: getTeamsWithPermissions,
        variables: {
          input: input,
        },
      });
      const teamsData = result.data.getTeamsWithPermissions;
      if (teamsData.length > 0) {
        // Deep-link: honour ?team= URL param so the correct team is set
        // from the very first render, avoiding a flash of the wrong team.
        const params = new URLSearchParams(window.location.search);
        const teamParam = params.get("team");
        if (teamParam) {
          const paramLower = teamParam.toLowerCase();
          const veslPrefixed = `vesl - ${paramLower}`;
          const matched = teamsData.find((t: Team) => {
            const nameLower = t.name.toLowerCase();
            return nameLower === paramLower || nameLower === veslPrefixed;
          });
          if (matched) return matched;
        }
        // Fall back to default team, then first team
        const defaultTeam = teamsData.find((t: Team) => t.is_default === true);
        return defaultTeam || teamsData[0];
      }
    } catch (err) {
      console.error(err);
    }
    team.id = 0;
    return team; // default team id that doesn't exist
  };

  async function checkUser() {
    try {
      const authSession = await fetchAuthSession({ forceRefresh: true });
      const currentUser = await getCurrentUser();
      // Restore deep-link URL that was stashed before the OAuth redirect.
      // Cognito drops the original ?query=... on callback, so we saved it
      // to sessionStorage in setPostAuthRedirect() and replay it here.
      consumePostAuthRedirect();
      setCurrentUser(currentUser);
      setUser(currentUser.username.split("_")[1]);
      setAuthSession(authSession);
    } catch (error) {
      console.error("Authentication failed", error);
      setCurrentUser(null);
      setAuthSession(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user === "") {
      checkUser().then();
    } else {
      getTeams(user).then((team) => {
        setdefaultTeamId(team.id);
        setGlobalTeamId(team.id);
        setTeamConfig(team);
      });
    }
  }, [user]);

  if (loading) {
    return createMainPage();
  }
  if (!currentUser) {
    try {
      setPostAuthRedirect();
      signInWithRedirect({
        provider: {
          custom: "AmazonFederate",
        },
      });
    } catch (error) {
      console.error("Authentication failed", error);
    }
  }
  return createMainPage();
};

export default App;
